import { useMemo } from 'react';

import {
  useGetCommonStockCalculation,
  useGetLongTermDebtCalculation,
  useGetLongTermInvestmentsCalculation,
  useGetOtherNonCurrentAssetsCalculation,
  useGetRetainedEarningsCalculation,
  useGetTotalAssetsCalculation,
  useGetTotalCurrentAssetsCalculation,
  useGetTotalCurrentLiabilitiesCalculation,
  useGetTotalEquityCalculation,
  useGetTotalLiabilitiesEquityCalculation,
  useGetTotalNonCurrentAssetsCalculation,
  useGetTotalNonCurrentLiabilitiesCalculation,
  usePpeAssetsCalculation,
  useRecalculateOnUnitChange
} from './balanceSheet';
import {
  useCashReceivedFromCustomers,
  useCashToSuppliers,
  useGetCashFlowLiabilitiesCalculation,
  useGetChangingDeferredTaxCalculation,
  useGetChangingSalesAssetsCalculation,
  useGetFinancialActivitiesNetCashCalculation,
  useGetInvestingActivitiesNetCashCalculation,
  useGetOperatingActivitiesNetCashCalculation,
  useGetTotalDirectNetCashCalculation,
  useGetTotalInIndirectNetCashCalculation,
} from './cashFlow';
import { useTotalExpansesCalculation } from './expensesPlan/useTotalExpansesCalculation';
import {
  usePerformanceEfficiency,
  usePerformanceInvestment,
  usePerformanceLiquidity,
  usePerformanceProfitability,
  usePerformanceSolvency,
} from './performance';
import { usePersonalExpensesCalculation } from './personalExpenses/usePersonalExpensesCalculation';
import { useProductionCalculation } from './production/useProductionCalculation';
import { useProfitAndLossCalculation } from './profitAndLoss/useProfitAndLossCalculation';
import { useRevenuePlanCalculation } from './revenuePlan/useRevenuePlanCalculation';
import { useSalesPlanCalculation } from './salesPlan/useSalesPlanCalculation';
import { IFormContentTypes, IObservedFormsType, OBSERVED_FORMS } from '../utils/form';

export const useHandleCalculation = (formName: IObservedFormsType) => {
  const salesPlanCalculationHandler = useSalesPlanCalculation();
  const revenuePlanCalculationHandler = useRevenuePlanCalculation();
  const profitAndLossCalculationHandler = useProfitAndLossCalculation();
  const totalExpansesHandler = useTotalExpansesCalculation();
  const personalExpensesHandler = usePersonalExpensesCalculation();
  const customerCashHandler = useCashReceivedFromCustomers();
  const supplierCashHandler = useCashToSuppliers();
  const operatingActivitiesNetCashHandler = useGetOperatingActivitiesNetCashCalculation();
  const financialActivitiesNetCashHandler = useGetFinancialActivitiesNetCashCalculation();
  const investingActivitiesNetCashHandler = useGetInvestingActivitiesNetCashCalculation();
  const totalDirectNetCashHandler = useGetTotalDirectNetCashCalculation();
  const productionHandler = useProductionCalculation();
  const currentAssetsHandler = useGetTotalCurrentAssetsCalculation();
  const nonCurrentAssetsHandler = useGetTotalNonCurrentAssetsCalculation();
  const currentLiabilitiesHandler = useGetTotalCurrentLiabilitiesCalculation();
  const nonCurrentLiabilitiesHandler = useGetTotalNonCurrentLiabilitiesCalculation();
  const equityHandler = useGetTotalEquityCalculation();
  const assetsHandler = useGetTotalAssetsCalculation();
  const liabilitiesEquityHandler = useGetTotalLiabilitiesEquityCalculation();
  const ppeAssetsHandler = usePpeAssetsCalculation();
  const longTermInvestmentsHandler = useGetLongTermInvestmentsCalculation();
  const longTermDebtHandler = useGetLongTermDebtCalculation();
  const commonStockHandler = useGetCommonStockCalculation();
  const retainedEarningsHandler = useGetRetainedEarningsCalculation();
  const performanceProfitabilityHandler = usePerformanceProfitability();
  const performanceEfficiencyHandler = usePerformanceEfficiency();
  const performanceInvestmentHandler = usePerformanceInvestment();
  const performanceLiquidityHandler = usePerformanceLiquidity();
  const performanceSolvencyHandler = usePerformanceSolvency();
  const deferredTaxHandler = useGetChangingDeferredTaxCalculation();
  const salesAssetsHandler = useGetChangingSalesAssetsCalculation();
  const liabilitiesHandler = useGetCashFlowLiabilitiesCalculation();
  const totalIndirectNetCashHandler = useGetTotalInIndirectNetCashCalculation();
  const recalculateOnUnitUpdate = useRecalculateOnUnitChange();
  const otherNonCurrentAssetsHandler = useGetOtherNonCurrentAssetsCalculation();

  const formsToFunctionsMap: Record<
    IObservedFormsType,
    (
      content: IFormContentTypes[IObservedFormsType],
      previousContent: IFormContentTypes[IObservedFormsType],
    ) => Promise<void> | void
  > = useMemo(
    () => ({
      [OBSERVED_FORMS.SALES_PLAN]: (content, previousContent) =>
        salesPlanCalculationHandler(
          content as IFormContentTypes['salesPlan'],
          previousContent as IFormContentTypes['salesPlan'],
        ),
      [OBSERVED_FORMS.REVENUE_PLAN]: (content, previousContent) =>
        revenuePlanCalculationHandler(
          content as IFormContentTypes['revenuePlan'],
          previousContent as IFormContentTypes['revenuePlan'],
        ),
      [OBSERVED_FORMS.PROFIT_AND_LOSS]: (content) =>
        profitAndLossCalculationHandler(content as IFormContentTypes['profitAndLoss']),
      [OBSERVED_FORMS.EXPENSES_MONTHLY]: (content) =>
        totalExpansesHandler(content as IFormContentTypes['expensesMonthly']),
      [OBSERVED_FORMS.PERSONAL_EXPENSES_BY_CATEGORY]: (content) =>
        personalExpensesHandler(content as IFormContentTypes['personalExpensesByCategory']),
      [OBSERVED_FORMS.CASH_FLOW_FROM_CUSTOMERS]: (content) =>
        customerCashHandler(content as IFormContentTypes['cashFlowFromCustomers']),
      [OBSERVED_FORMS.CASH_FLOW_TO_SUPPLIERS]: (content) =>
        supplierCashHandler(content as IFormContentTypes['cashFlowToSuppliers']),
      [OBSERVED_FORMS.OPERATING_ACTIVITIES_NET_CASH]: (content) =>
        operatingActivitiesNetCashHandler(content as IFormContentTypes['operatingActivitiesNetCash']),
      [OBSERVED_FORMS.FINANCIAL_ACTIVITIES_NET_CASH]: (content) =>
        financialActivitiesNetCashHandler(content as IFormContentTypes['financialActivitiesNetCash']),
      [OBSERVED_FORMS.INVESTING_ACTIVITIES_NET_CASH]: (content) =>
        investingActivitiesNetCashHandler(content as IFormContentTypes['investingActivitiesNetCash']),
      [OBSERVED_FORMS.TOTAL_NET_CASH]: (content) =>
        totalDirectNetCashHandler(content as IFormContentTypes['totalDirectNetCash']),
      [OBSERVED_FORMS.PRODUCTION]: (content) => productionHandler(content as IFormContentTypes['production']),
      [OBSERVED_FORMS.CURRENT_ASSETS_TOTAL]: (content) =>
        currentAssetsHandler(content as IFormContentTypes['currentAssetsTotal']),
      [OBSERVED_FORMS.NON_CURRENT_ASSETS_TOTAL]: (content) =>
        nonCurrentAssetsHandler(content as IFormContentTypes['nonCurrentAssetsTotal']),
      [OBSERVED_FORMS.CURRENT_LIABILITIES_TOTAL]: (content) =>
        currentLiabilitiesHandler(content as IFormContentTypes['currentLiabilitiesTotal']),
      [OBSERVED_FORMS.NON_CURRENT_LIABILITIES_TOTAL]: (content) =>
        nonCurrentLiabilitiesHandler(content as IFormContentTypes['nonCurrentLiabilitiesTotal']),
      [OBSERVED_FORMS.EQUITY_TOTAL]: (content) => equityHandler(content as IFormContentTypes['equityTotal']),
      [OBSERVED_FORMS.TOTAL_ASSETS]: (content) => assetsHandler(content as IFormContentTypes['totalAssets']),
      [OBSERVED_FORMS.TOTAL_LIABILITIES_EQUITY]: (content) =>
        liabilitiesEquityHandler(content as IFormContentTypes['totalLiabilitiesEquity']),
      [OBSERVED_FORMS.PPE_ASSETS]: (content) => ppeAssetsHandler(content as IFormContentTypes['ppeAssets']),
      [OBSERVED_FORMS.LONG_TERM_INVESTMENTS]: (content) =>
        longTermInvestmentsHandler(content as IFormContentTypes['longTermInvestments']),
      [OBSERVED_FORMS.LONG_TERM_DEBT]: (content) => longTermDebtHandler(content as IFormContentTypes['longTermDebt']),
      [OBSERVED_FORMS.COMMON_STOCK]: (content) => commonStockHandler(content as IFormContentTypes['commonStock']),
      [OBSERVED_FORMS.RETAINED_EARNINGS]: (content) =>
        retainedEarningsHandler(content as IFormContentTypes['retainedEarnings']),
      [OBSERVED_FORMS.PERFORMANCE_PROFITABILITY]: (content) =>
        performanceProfitabilityHandler(content as IFormContentTypes['performanceProfitability']),
      [OBSERVED_FORMS.PERFORMANCE_EFFICIENCY]: (content) =>
        performanceEfficiencyHandler(content as IFormContentTypes['performanceEfficiency']),
      [OBSERVED_FORMS.PERFORMANCE_INVESTMENT]: (content) =>
        performanceInvestmentHandler(content as IFormContentTypes['performanceInvestment']),
      [OBSERVED_FORMS.PERFORMANCE_LIQUIDITY]: (content) =>
        performanceLiquidityHandler(content as IFormContentTypes['performanceLiquidity']),
      [OBSERVED_FORMS.PERFORMANCE_SOLVENCY]: (content) =>
        performanceSolvencyHandler(content as IFormContentTypes['performanceSolvency']),
      [OBSERVED_FORMS.DEFERRED_TAX_CHANGES]: (content) =>
        deferredTaxHandler(content as IFormContentTypes['deferredTaxChanges']),
      [OBSERVED_FORMS.SELLING_ASSETS]: (content) => salesAssetsHandler(content as IFormContentTypes['sellingAssets']),
      [OBSERVED_FORMS.CASH_FLOW_LIABILITIES]: (content) =>
        liabilitiesHandler(content as IFormContentTypes['cashFlowLiabilities']),
      [OBSERVED_FORMS.TOTAL_INDIRECT_NET_CASH]: (content) =>
        totalIndirectNetCashHandler(content as IFormContentTypes['totalIndirectNetCash']),
      [OBSERVED_FORMS.CURRENT_UNIT]: (content) =>
        recalculateOnUnitUpdate(content as IFormContentTypes['currentUnit']),
      [OBSERVED_FORMS.OTHER_NON_CURRENT_ASSETS]: (content) =>
        otherNonCurrentAssetsHandler(content as IFormContentTypes['otherNonCurrentAssets']),
    }),
    [
      salesPlanCalculationHandler,
      revenuePlanCalculationHandler,
      totalExpansesHandler,
      profitAndLossCalculationHandler,
      personalExpensesHandler,
      customerCashHandler,
      supplierCashHandler,
      operatingActivitiesNetCashHandler,
      financialActivitiesNetCashHandler,
      investingActivitiesNetCashHandler,
      totalDirectNetCashHandler,
      productionHandler,
      currentAssetsHandler,
      nonCurrentAssetsHandler,
      currentLiabilitiesHandler,
      nonCurrentLiabilitiesHandler,
      equityHandler,
      assetsHandler,
      liabilitiesEquityHandler,
      ppeAssetsHandler,
      longTermInvestmentsHandler,
      longTermDebtHandler,
      commonStockHandler,
      retainedEarningsHandler,
      performanceProfitabilityHandler,
      performanceEfficiencyHandler,
      performanceInvestmentHandler,
      performanceLiquidityHandler,
      performanceSolvencyHandler,
      deferredTaxHandler,
      salesAssetsHandler,
      liabilitiesHandler,
      totalIndirectNetCashHandler,
      recalculateOnUnitUpdate,
      otherNonCurrentAssetsHandler
    ],
  );

  return formsToFunctionsMap[formName];
};
